import { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

import { JsonObject } from '../types/jsonObject';
import {
  createServiceOSAxiosClient,
  SERVICE_OS_API_AUTH_COOKIE_NAME,
} from './serviceOSAxiosClient';

enum ContractorId {
  UK = 1,
  FR = 3,
}

interface ServiceOSApiResponse {
  data?: unknown | null;
  error?: unknown | null;
  success?: unknown | null;
}

interface ServiceOSApiClientResponse<TData> {
  data: TData | null;
  successMessage: string | null;
  errorMessage: string | null;
}

export interface ServiceOSRegisterRequestPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  password: string;
  referrer_code?: string;
  contractor_id?: ContractorId;
  preferences?: {
    allow_mk_all?: number;
  };
}

const getContractorIdByLocale = (locale: string | undefined) => {
  if (locale === 'en-gb') return ContractorId.UK;
  if (locale === 'fr-fr') return ContractorId.FR;
};

const saveServiceOSApiAuthCookie = (sid: string, expiresAtSeconds: number) => {
  const date = new Date(0);
  date.setUTCSeconds(expiresAtSeconds);

  Cookies.set(SERVICE_OS_API_AUTH_COOKIE_NAME, sid, { expires: date });
};

const getAuthSessionDataFromResponse = (response: AxiosResponse) => {
  const sid = response.data?.data?.session?.sid;
  const expires = response.data?.data?.session?.expire_time;

  return {
    sid,
    expires,
  };
};

const getSuccessMessageFromResponse = (response: AxiosResponse): string | null => {
  const success = response?.data?.success || null;

  if (!success) {
    return null;
  }

  return (Array.isArray(success) ? success[0]?.message : success.message) || null;
};

const getErrorMessageFromResponse = (response: AxiosResponse): string | null => {
  const error = response?.data?.error || response?.data?.errors || null;

  if (!error) {
    return null;
  }

  return (Array.isArray(error) ? error[0]?.message : error.message) || null;
};

const buildResponse = <TData extends ServiceOSApiResponse>(
  response: AxiosResponse<TData>
): ServiceOSApiClientResponse<TData> => {
  const data = response.data?.data || null;
  return {
    data: Array.isArray(data) ? data[0] : data,
    successMessage: getSuccessMessageFromResponse(response),
    errorMessage: getErrorMessageFromResponse(response),
  };
};

export class ServiceOSApiClient {
  private readonly axios = createServiceOSAxiosClient();

  async register(data: ServiceOSRegisterRequestPayload, locale?: string) {
    try {
      const response = await this.axios.post('unit/register', {
        ...data,
        contractor_id: getContractorIdByLocale(locale),
      });
      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async verifyEmail(email: string, verificationToken: string) {
    try {
      const response = await this.axios.post('unit/verify_email', {
        email,
        token: verificationToken,
      });

      const { sid, expires } = getAuthSessionDataFromResponse(response);

      if (sid) {
        this.setAxiosInstanceAuthorizationHeader(sid);
        saveServiceOSApiAuthCookie(sid, expires);
      }

      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async generateFountainLink() {
    try {
      const response = await this.axios.get('/external/integrations/fountain/application');
      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async login(username: string, password: string) {
    try {
      const response = await this.axios.post('unit/login', { username, password });

      const { sid, expires } = getAuthSessionDataFromResponse(response);

      if (sid) {
        this.setAxiosInstanceAuthorizationHeader(sid);
        saveServiceOSApiAuthCookie(sid, expires);
      }

      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async requestResetPassword(email: string) {
    try {
      const response = await this.axios.post('unit/request_reset_password', { email });
      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async changePassword(token: string, password: string, confirmPassword: string) {
    try {
      const response = await this.axios.post('unit/reset_password', {
        token,
        password,
        confirm_password: confirmPassword,
      });
      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  async resendEmailVerification(email: string) {
    try {
      const response = await this.axios.post('unit/send_verification', {
        username: email,
      });
      return buildResponse(response);
    } catch (error) {
      console.error(error.message);
      return buildResponse(error.response);
    }
  }

  private setAxiosInstanceAuthorizationHeader(sid: string) {
    this.axios.defaults.headers.common.Authorization = `Bearer ${sid}`;
  }
}
